import type { UseQueryResult } from '@tanstack/react-query';
import { QueryCache, QueryClient, useQuery } from '@tanstack/react-query';

import type { Meta } from './types';

export type Params = Record<string, string | number | boolean | null | undefined>;

export const API_DOWN = "Can't reach the usage API on 127.0.0.1:8300. Is it running? (python -m usage api)";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

export function toQuery(params?: Params): string {
  const query = new URLSearchParams();
  Object.entries(params ?? {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') query.set(key, String(value));
  });
  const text = query.toString();
  return text ? `?${text}` : '';
}

async function request<T>(path: string, init: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`/api${path}`, { credentials: 'same-origin', ...init });
  } catch {
    throw new ApiError(0, API_DOWN);
  }
  if (!response.ok) {
    let detail: string | null = null;
    try {
      const body = (await response.json()) as { detail?: unknown };
      if (typeof body.detail === 'string') detail = body.detail;
    } catch {
      // not JSON
    }
    if (detail === null) detail = response.status >= 500 ? API_DOWN : response.statusText || `HTTP ${response.status}`;
    throw new ApiError(response.status, detail);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export function getJson<T>(path: string, params?: Params): Promise<T> {
  return request<T>(`${path}${toQuery(params)}`, {});
}

export function postJson<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, {
    method: 'POST',
    headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

export const queryClient: QueryClient = new QueryClient({
  // An expired session shows up as a 401 on any query: refetch /meta so the shell lands on the sign-in screen.
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (error instanceof ApiError && error.status === 401 && query.queryKey[0] !== '/meta') {
        void queryClient.invalidateQueries({ queryKey: ['/meta'] });
      }
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 2,
    },
  },
});

export function useApi<T>(
  path: string,
  params?: Params,
  options?: { enabled?: boolean; refetchInterval?: number },
): UseQueryResult<T, Error> {
  return useQuery<T, Error>({ queryKey: [path, params ?? {}], queryFn: () => getJson<T>(path, params), ...options });
}

export function useMeta(): UseQueryResult<Meta, Error> {
  return useApi<Meta>('/meta', undefined, { refetchInterval: 60_000 });
}
